<template>
  <div class="search-container">
    <div class="search-box" :class="{ 'expanded': isExpanded }">
      <div class="search-input-wrapper">
        <input
          ref="searchInput"
          v-model="searchQuery"
          type="text"
          placeholder="搜索文章、标签..."
          class="search-input"
          @focus="handleFocus"
          @blur="handleBlur"
          @input="handleSearch"
          @keydown.escape="clearSearch"
          @keydown.enter="selectFirstResult"
        >
        <button class="search-button" @click="toggleSearch">
          <i class="fas fa-search" v-if="!searchQuery"></i>
          <i class="fas fa-times" v-else @click.stop="clearSearch"></i>
        </button>
      </div>
      
      <!-- 搜索结果容器 -->
      <div v-if="showResults && isExpanded" class="search-results-container">
        <!-- 有搜索结果 -->
        <div v-if="searchQuery && searchResults.length > 0" class="search-results">
          <div class="search-results-header">
            <span>找到 {{ searchResults.length }} 个结果</span>
          </div>
          <div class="search-results-list">
            <router-link
              v-for="result in searchResults"
              :key="result.id"
              :to="`/blog/${result.id}`"
              class="search-result-item"
              @click="handleResultClick"
            >
              <div class="result-title">{{ result.title }}</div>
              <div class="result-summary">{{ result.summary }}</div>
              <div class="result-meta">
                <span class="result-date">{{ formatDate(result.date) }}</span>
                <div class="result-tags">
                  <span v-for="tag in result.tags" :key="tag" class="result-tag">
                    {{ tag }}
                  </span>
                </div>
              </div>
            </router-link>
          </div>
        </div>
        
        <!-- 无结果提示 -->
        <div v-else-if="searchQuery && searchResults.length === 0" class="no-results">
          <i class="fas fa-search"></i>
          <p>没有找到相关文章</p>
          <span>尝试使用其他关键词</span>
        </div>
        
        <!-- 搜索建议（仅在没有输入时显示） -->
        <div v-else-if="!searchQuery" class="search-suggestions">
          <div class="suggestion-section">
            <h4>热门标签</h4>
            <div class="suggestion-tags">
              <span
                v-for="tag in popularTags"
                :key="tag"
                class="suggestion-tag"
                @click="searchByTag(tag)"
              >
                {{ tag }}
              </span>
            </div>
          </div>
          <div class="suggestion-section">
            <h4>最新文章</h4>
            <div class="suggestion-articles">
              <router-link
                v-for="article in recentArticles"
                :key="article.id"
                :to="`/blog/${article.id}`"
                class="suggestion-article"
                @click="handleResultClick"
              >
                <span class="article-title">{{ article.title }}</span>
                <span class="article-date">{{ formatDate(article.date) }}</span>
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 搜索遮罩 -->
    <div v-if="isExpanded" class="search-overlay" @click="closeSearch"></div>
  </div>
</template>

<script>
import { blogArticles } from '@/utils/blog.js'

export default {
  name: 'SearchBox',
  emits: ['articleSelected'],
  data() {
    return {
      searchQuery: '',
      isExpanded: false,
      showResults: false,
      searchResults: [],
      allArticles: blogArticles
    }
  },
  computed: {
    popularTags() {
      const tagCount = {}
      this.allArticles.forEach(article => {
        article.tags.forEach(tag => {
          tagCount[tag] = (tagCount[tag] || 0) + 1
        })
      })
      return Object.keys(tagCount)
        .sort((a, b) => tagCount[b] - tagCount[a])
        .slice(0, 6)
    },
    recentArticles() {
      return [...this.allArticles]
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .slice(0, 3)
    }
  },
  methods: {
    toggleSearch() {
      this.isExpanded = !this.isExpanded
      if (this.isExpanded) {
        this.$nextTick(() => {
          this.$refs.searchInput.focus()
        })
        this.showResults = true
      } else {
        this.closeSearch()
      }
    },
    
    handleFocus() {
      this.isExpanded = true
      this.showResults = true
    },
    
    handleBlur() {
      // 延迟关闭，允许点击搜索结果
      setTimeout(() => {
        if (!this.$refs.searchInput.matches(':focus')) {
          this.showResults = false
        }
      }, 200)
    },
    
    handleSearch() {
      if (!this.searchQuery.trim()) {
        this.searchResults = []
        return
      }
      
      const query = this.searchQuery.toLowerCase().trim()
      this.searchResults = this.allArticles.filter(article => {
        return (
          article.title.toLowerCase().includes(query) ||
          article.summary.toLowerCase().includes(query) ||
          article.tags.some(tag => tag.toLowerCase().includes(query))
        )
      })
    },
    
    clearSearch() {
      this.searchQuery = ''
      this.searchResults = []
      this.isExpanded = false
      this.showResults = false
    },
    
    closeSearch() {
      this.isExpanded = false
      this.showResults = false
    },
    
    selectFirstResult() {
      if (this.searchResults.length > 0) {
        this.$router.push(`/blog/${this.searchResults[0].id}`)
        this.closeSearch()
        this.$emit('articleSelected')
      }
    },
    
    handleResultClick() {
      this.closeSearch()
      this.$emit('articleSelected')
    },
    
    searchByTag(tag) {
      this.searchQuery = tag
      this.handleSearch()
    },
    
    formatDate(dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/styles/variables.scss";

.search-container {
  position: relative;
  z-index: 1000;
}

.search-box {
  position: relative;
  width: 300px;
  max-width: 100%;
  transition: all 0.3s ease;
  
  &.expanded {
    width: 400px;
    
    @media (max-width: 768px) {
      width: 100vw;
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      z-index: 1001;
      padding: 20px;
      background: white;
      box-shadow: 0 2px 20px rgba(0, 0, 0, 0.1);
    }
  }
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.9);
  border: 2px solid transparent;
  border-radius: 25px;
  overflow: hidden;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  
  &:focus-within {
    border-color: #667eea;
    box-shadow: 0 0 20px rgba(102, 126, 234, 0.3);
  }
}

.search-input {
  flex: 1;
  padding: 12px 20px;
  border: none;
  background: transparent;
  font-size: 16px;
  color: #2c3e50;
  outline: none;
  
  &::placeholder {
    color: #7f8c8d;
  }
}

.search-button {
  padding: 12px 16px;
  background: none;
  border: none;
  color: #667eea;
  font-size: 18px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    color: #5a6fd8;
    transform: scale(1.1);
  }
}

.search-results-container {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  margin-top: 8px;
}

.search-results {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  max-height: 400px;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #ddd;
    border-radius: 3px;
  }
}

.search-results-header {
  padding: 12px 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  font-size: 14px;
  color: #6c757d;
  font-weight: 500;
}

.search-results-list {
  padding: 8px 0;
}

.search-result-item {
  display: block;
  padding: 16px 20px;
  text-decoration: none;
  color: inherit;
  border-bottom: 1px solid #f1f3f4;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f8f9fa;
    transform: translateX(4px);
  }
  
  &:last-child {
    border-bottom: none;
  }
}

.result-title {
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 4px;
  line-height: 1.4;
}

.result-summary {
  font-size: 14px;
  color: #5a6c7d;
  line-height: 1.5;
  margin-bottom: 8px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.result-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
}

.result-date {
  color: #95a5a6;
}

.result-tags {
  display: flex;
  gap: 6px;
}

.result-tag {
  background: #e3f2fd;
  color: #1976d2;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
}

.no-results {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  padding: 40px 20px;
  text-align: center;
  color: #7f8c8d;
  
  i {
    font-size: 48px;
    margin-bottom: 16px;
    opacity: 0.5;
  }
  
  p {
    font-size: 18px;
    margin-bottom: 8px;
    color: #2c3e50;
  }
  
  span {
    font-size: 14px;
  }
}

.search-suggestions {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  padding: 20px;
}

.suggestion-section {
  margin-bottom: 24px;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  h4 {
    margin: 0 0 12px 0;
    font-size: 14px;
    color: #2c3e50;
    font-weight: 600;
  }
}

.suggestion-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.suggestion-tag {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }
}

.suggestion-articles {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.suggestion-article {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 8px;
  text-decoration: none;
  color: inherit;
  transition: all 0.2s ease;
  
  &:hover {
    background: #e9ecef;
    transform: translateX(4px);
  }
}

.article-title {
  font-size: 14px;
  color: #2c3e50;
  font-weight: 500;
}

.article-date {
  font-size: 12px;
  color: #95a5a6;
}

.search-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 999;
  
  @media (min-width: 769px) {
    display: none;
  }
}

// 响应式设计
@media (max-width: 768px) {
  .search-box {
    width: 250px;
    
    &.expanded {
      .search-results {
        max-height: calc(100vh - 120px);
      }
    }
  }
}

// 深色模式适配
@media (prefers-color-scheme: dark) {
  .search-input-wrapper {
    background: rgba(30, 30, 30, 0.9);
  }
  
  .search-input {
    color: #e8eaed;
    
    &::placeholder {
      color: #9aa0a6;
    }
  }
  
  .search-results {
    background: #1a1a1a;
  }
  
  .search-results-header {
    background: #2d2d2d;
    border-bottom-color: #404040;
    color: #bbb;
  }
  
  .search-result-item {
    border-bottom-color: #404040;
    
    &:hover {
      background: #2d2d2d;
    }
  }
  
  .result-title {
    color: #e8eaed;
  }
  
  .result-summary {
    color: #bbb;
  }
  
  .no-results {
    color: #9aa0a6;
    
    p {
      color: #e8eaed;
    }
  }
  
  .suggestion-section h4 {
    color: #e8eaed;
  }
  
  .suggestion-article {
    background: #2d2d2d;
    
    &:hover {
      background: #404040;
    }
  }
  
  .article-title {
    color: #e8eaed;
  }
}
</style>
